import request from "@/utils/request";

function getRequestOptions(options = {}) {
  return {
    localErrorState: true,
    ...options,
  };
}

function getWriteRequestOptions(options = {}) {
  const { idempotency_key, headers, ...request_options } = options;
  return {
    localErrorState: true,
    ...request_options,
    headers: {
      ...headers,
      ...(idempotency_key ? { "Idempotency-Key": idempotency_key } : {}),
    },
  };
}

function pathSegment(value) {
  return encodeURIComponent(String(value));
}

function sheetPath(sheet_id, suffix = "") {
  return `/sheets/${pathSegment(sheet_id)}${suffix}`;
}

export function getSheets(params = {}, options = {}) {
  return request({
    url: "/sheets",
    method: "get",
    params,
    ...getRequestOptions(options),
  });
}

export function createSheet(data, options = {}) {
  return request({
    url: "/sheets",
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheet(sheet_id, options = {}) {
  return request({
    url: sheetPath(sheet_id),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function updateSheet(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id),
    method: "patch",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function deleteSheet(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id),
    method: "delete",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheetUserSettings(sheet_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/user-settings"),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function saveSheetUserSettings(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/user-settings"),
    method: "put",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheetUserColumns(sheet_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/user-columns"),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function saveSheetUserColumns(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/user-columns"),
    method: "put",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function querySheetContacts(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/query"),
    method: "post",
    data,
    ...getRequestOptions(options),
  });
}

export function addSheetMembership(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/memberships"),
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function removeSheetMembership(
  sheet_id,
  contact_id,
  data,
  options = {}
) {
  return request({
    url: sheetPath(sheet_id, `/memberships/${pathSegment(contact_id)}`),
    method: "delete",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function updateContactField(
  sheet_id,
  contact_id,
  field_id,
  data,
  options = {}
) {
  return request({
    url: sheetPath(
      sheet_id,
      `/contacts/${pathSegment(contact_id)}/fields/${pathSegment(field_id)}`
    ),
    method: "patch",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function deleteWorkspaceContact(
  sheet_id,
  contact_id,
  data,
  options = {}
) {
  return request({
    url: sheetPath(sheet_id, `/contacts/${pathSegment(contact_id)}`),
    method: "delete",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function deleteSheetField(field_id, data, options = {}) {
  return request({
    url: `/sheets/fields/${pathSegment(field_id)}`,
    method: "delete",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheetRules(sheet_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/rules"),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function createSheetRule(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/rules"),
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function applySheetRule(sheet_id, rule_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, `/rules/${pathSegment(rule_id)}/apply`),
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function createSheetImport(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/imports"),
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheetImport(sheet_id, job_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, `/imports/${pathSegment(job_id)}`),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function createSheetExport(sheet_id, data, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/exports"),
    method: "post",
    data,
    ...getWriteRequestOptions(options),
  });
}

export function getSheetExport(sheet_id, job_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, `/exports/${pathSegment(job_id)}`),
    method: "get",
    ...getRequestOptions(options),
  });
}

export function getSheetDeliveries(sheet_id, options = {}) {
  return request({
    url: sheetPath(sheet_id, "/deliveries"),
    method: "get",
    ...getRequestOptions(options),
  });
}

export const sheetsProvider = Object.freeze({
  getSheets,
  createSheet,
  getSheet,
  updateSheet,
  deleteSheet,
  getSheetUserSettings,
  saveSheetUserSettings,
  getSheetUserColumns,
  saveSheetUserColumns,
  querySheetContacts,
  addSheetMembership,
  removeSheetMembership,
  updateContactField,
  deleteWorkspaceContact,
  deleteSheetField,
  getSheetRules,
  createSheetRule,
  applySheetRule,
  createSheetImport,
  getSheetImport,
  createSheetExport,
  getSheetExport,
  getSheetDeliveries,
});
